import { useEffect, useRef } from "react";

import { API_BASE as API_GATEWAY } from "../config/api";
import "./dashboard-v2.css";

/**
 * Standalone "CS Dashboard" (route: /dashboard/v2).
 *
 * This is a faithful, self-contained port of the original standalone HTML
 * dashboard. It is intentionally kept separate from the main MUI/Tailwind
 * dashboard: all of its styling lives in `dashboard-v2.css` scoped under
 * `.dashboard-v2-root`, and all of its runtime logic runs inside the effect
 * below against the rendered markup — nothing here touches the rest of the app.
 *
 * The dashboard data is fetched from the public API Gateway dashboard endpoint
 * (no authentication required).
 */

// Endpoints derived from the API Gateway base URL (VITE_API_ENDPOINT, exposed
// via config/api.ts) instead of being hardcoded.
const API_BASE = `${API_GATEWAY.GATEWAY}/dashboard`;
const API_ONLINE = `${API_GATEWAY.GATEWAY}/online-users`;
const DIABLO_WORKER =
  (import.meta.env.VITE_DIABLO_WORKER_URL as string) ||
  "https://msgofd.messenger-of-death14.workers.dev";

// Markup ported verbatim from the source (body only). The original token modal
// and "🔑 Token" button were removed — the dashboard API is public.
const BODY_HTML = `
<!-- TOPBAR -->
<div class="topbar">
  <div class="brand">
    <div class="brand-logo">EL</div>
    <div>
      <div class="brand-name">Essilor Luxottica</div>
      <div class="brand-sub">Customer Service Dashboard</div>
    </div>
  </div>
  <div class="topbar-right">
    <span class="refresh-info" id="refreshInfo">—</span>
    <div class="date-pill" id="dateDisplay">—</div>
    <div class="live-badge">Live</div>
  </div>
</div>

<div class="notice">* ข้อมูลบน Dashboard อาจมีการ Delay ประมาณ 1 นาที *</div>

<div class="status-bar">
  <span id="statusBadge" class="status-loading">⏳ กำลังโหลด...</span>
  <span id="lastUpdate" style="font-size:11px;color:var(--text3);font-family:var(--mono);"></span>
</div>

<div class="layout">

  <!-- LEFT -->
  <div class="left-panel">
    <!-- ONLINE USERS -->
    <div class="card" id="onlineCard">
      <div class="card-label" style="display:flex;align-items:center;justify-content:space-between;">
        <span>Online Now</span>
        <span id="onlineCount" style="font-family:var(--mono);font-size:11px;background:var(--green-lt);color:var(--green);border:1px solid #a7f3d0;border-radius:20px;padding:1px 8px;">—</span>
      </div>
      <div id="onlineList" style="display:flex;flex-direction:column;gap:6px;">
        <div style="font-size:11px;color:var(--text3);text-align:center;padding:8px 0;">กำลังโหลด...</div>
      </div>
    </div>

    <!-- DIABOLOCOM AGENT STATUS -->
    <div class="card" id="diabloCard">
      <div class="card-label" style="display:flex;align-items:center;justify-content:space-between;">
        <span>📞 Agent Status</span>
        <span id="diabloCount" style="font-family:var(--mono);font-size:11px;background:rgba(59,130,246,.15);color:var(--blue);border:1px solid rgba(59,130,246,.3);border-radius:20px;padding:1px 8px;">—</span>
      </div>
      <div id="diabloList" style="display:flex;flex-direction:column;gap:5px;">
        <div style="font-size:11px;color:var(--text3);text-align:center;padding:8px 0;">กำลังโหลด...</div>
      </div>
    </div>

  </div><!-- end left-panel -->

  <!-- RIGHT -->
  <div class="right-panel">

    <!-- TASK CREATOR + SUPPORT side by side -->
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;align-items:start;">

    <!-- TASK CREATOR -->
    <div style="display:flex;flex-direction:column;gap:10px;">

    <!-- Total Task Today + Urgent Task Banner -->
    <div class="section-block" style="padding:14px 18px;">
      <div style="display:flex;align-items:center;gap:18px;">
        <!-- Left: Title + Total number -->
        <div style="flex-shrink:0;">
          <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:var(--text3);margin-bottom:4px;">Total Task Today</div>
          <div style="display:flex;align-items:baseline;gap:6px;">
            <span id="donutTotal" style="font-family:var(--mono);font-size:36px;font-weight:700;color:var(--text1);line-height:1;">—</span>
            <span style="font-size:12px;color:var(--text3);">Tasks</span>
          </div>
        </div>
        <!-- Donut -->
        <svg id="donutSvg" width="70" height="70" viewBox="0 0 120 120" style="flex-shrink:0;">
          <circle cx="60" cy="60" r="48" fill="none" stroke="#1c2333" stroke-width="13"/>
          <circle id="donutNormal" cx="60" cy="60" r="48" fill="none" stroke="#374151" stroke-width="13"
            stroke-dasharray="0 301.6" stroke-dashoffset="75.4" stroke-linecap="round"/>
          <circle id="donutUrgent" cx="60" cy="60" r="48" fill="none" stroke="#ef4444" stroke-width="13"
            stroke-dasharray="0 301.6" stroke-linecap="round"/>
        </svg>
        <!-- Normal / Urgent counts -->
        <div style="display:flex;flex-direction:column;gap:8px;flex:1;">
          <div style="display:flex;align-items:center;justify-content:space-between;font-size:12px;">
            <div style="display:flex;align-items:center;gap:6px;color:var(--text2);">
              <span style="width:8px;height:8px;border-radius:50%;background:#374151;display:inline-block;"></span>Normal
            </div>
            <span id="normalCount" style="font-family:var(--mono);font-size:14px;font-weight:600;color:var(--text1);">—</span>
          </div>
          <div style="display:flex;align-items:center;justify-content:space-between;font-size:12px;">
            <div style="display:flex;align-items:center;gap:6px;color:var(--text2);">
              <span style="width:8px;height:8px;border-radius:50%;background:#ef4444;display:inline-block;"></span>Urgent
            </div>
            <span id="urgentCount" style="font-family:var(--mono);font-size:14px;font-weight:600;color:var(--red);">—</span>
          </div>
        </div>
        <!-- Divider -->
        <div style="width:1px;height:52px;background:var(--border2);flex-shrink:0;"></div>
        <!-- Urgent Task: Completed / Pending -->
        <div style="flex-shrink:0;">
          <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:var(--text3);margin-bottom:8px;">Urgent Task</div>
          <div style="display:flex;gap:10px;">
            <div style="display:flex;align-items:center;gap:8px;background:rgba(52,211,153,.1);border:1px solid rgba(52,211,153,.25);border-radius:var(--r-sm);padding:7px 12px;">
              <span style="width:22px;height:22px;border-radius:var(--r-sm);background:rgba(52,211,153,.2);color:var(--green);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;">✓</span>
              <div>
                <div style="font-size:9px;text-transform:uppercase;letter-spacing:.05em;color:var(--text3);margin-bottom:1px;">Completed</div>
                <div id="urgentCompleted" style="font-family:var(--mono);font-size:22px;font-weight:700;color:var(--green);line-height:1;">—</div>
              </div>
            </div>
            <div style="display:flex;align-items:center;gap:8px;background:rgba(248,113,113,.1);border:1px solid rgba(248,113,113,.25);border-radius:var(--r-sm);padding:7px 12px;">
              <span style="width:22px;height:22px;border-radius:var(--r-sm);background:rgba(248,113,113,.2);color:var(--red);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;">!</span>
              <div>
                <div style="font-size:9px;text-transform:uppercase;letter-spacing:.05em;color:var(--text3);margin-bottom:1px;">Pending</div>
                <div id="urgentPending" style="font-family:var(--mono);font-size:22px;font-weight:700;color:var(--red);line-height:1;">—</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="section-block">
      <div class="section-head">
        <div class="section-head-ico" style="background:#ede9fe;">
          <svg viewBox="0 0 24 24" fill="none" stroke="#7c3aed" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="3" width="6" height="2" rx="1"/><path d="M9 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2"/><path d="m9 12 2 2 4-4"/>
          </svg>
        </div>
        <span class="section-head-title">Task Creator</span>
      </div>

      <div class="col-header-row">
        <div class="col-header-blank"></div>
        <div class="col-header-cell line-hdr">
          <div class="col-hdr-ico"><svg viewBox="0 0 24 24" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
          <div><div class="col-hdr-name">Line</div><div class="col-hdr-sub">Create Today: <span class="col-hdr-count" id="hdrLineTotal">—</span></div></div>
        </div>
        <div class="col-header-cell email-hdr">
          <div class="col-hdr-ico"><svg viewBox="0 0 24 24" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg></div>
          <div><div class="col-hdr-name">Email</div><div class="col-hdr-sub">Create Today: <span class="col-hdr-count" id="hdrEmailTotal">—</span></div></div>
        </div>
      </div>

      <!-- Create Yesterday (บรรทัดแรก) -->
      <div class="data-row">
        <div class="row-label-cell"><div class="row-label-text" style="color:var(--red);">Create Yesterday</div></div>
        <div class="row-data-cell line-cell">
          <div class="stat-grid c3">
            <div class="sbox"><div class="sbox-lbl lbl-red">Stock</div><div class="sbox-val red" id="L_allStock">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-red">RX</div><div class="sbox-val red" id="L_allRx">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-red">VIP</div><div class="sbox-val red" id="L_allVip">—</div></div>
          </div>
        </div>
        <div class="row-data-cell email-cell">
          <div class="stat-grid c3">
            <div class="sbox"><div class="sbox-lbl lbl-red">Stock</div><div class="sbox-val red" id="E_allStock">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-red">RX</div><div class="sbox-val red" id="E_allRx">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-red">VIP</div><div class="sbox-val red" id="E_allVip">—</div></div>
          </div>
        </div>
      </div>

      <!-- Create Today (บรรทัดสอง) -->
      <div class="data-row">
        <div class="row-label-cell"><div class="row-label-text" style="color:var(--text1);">Create Today</div></div>
        <div class="row-data-cell line-cell">
          <div class="stat-grid c3">
            <div class="sbox"><div class="sbox-lbl lbl-white">Stock</div><div class="sbox-val" id="L_todayStock">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-white">RX</div><div class="sbox-val" id="L_todayRx">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-white">VIP</div><div class="sbox-val" id="L_todayVip">—</div></div>
          </div>
        </div>
        <div class="row-data-cell email-cell">
          <div class="stat-grid c3">
            <div class="sbox"><div class="sbox-lbl lbl-white">Stock</div><div class="sbox-val" id="E_todayStock">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-white">RX</div><div class="sbox-val" id="E_todayRx">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-white">VIP</div><div class="sbox-val" id="E_todayVip">—</div></div>
          </div>
        </div>
      </div>

      <!-- Create Tomorrow -->
      <div class="data-row">
        <div class="row-label-cell"><div class="row-label-text" style="color:var(--blue);">Create Tomorrow</div></div>
        <div class="row-data-cell line-cell">
          <div class="stat-grid c3">
            <div class="sbox"><div class="sbox-lbl lbl-blue">Stock</div><div class="sbox-val blue" id="L_tmrStock">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-blue">RX</div><div class="sbox-val blue" id="L_tmrRx">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-blue">VIP</div><div class="sbox-val blue" id="L_tmrVip">—</div></div>
          </div>
        </div>
        <div class="row-data-cell email-cell">
          <div class="stat-grid c3">
            <div class="sbox"><div class="sbox-lbl lbl-blue">Stock</div><div class="sbox-val blue" id="E_tmrStock">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-blue">RX</div><div class="sbox-val blue" id="E_tmrRx">—</div></div>
            <div class="sbox"><div class="sbox-lbl lbl-blue">VIP</div><div class="sbox-val blue" id="E_tmrVip">—</div></div>
          </div>
        </div>
      </div>

      <!-- Key-In -->
      <div class="data-row">
        <div class="row-label-cell"><div class="row-label-text">Key-In</div></div>
        <div class="row-data-cell line-cell">
          <div class="stat-grid c2">
            <div class="sbox"><div class="sbox-lbl">Waiting</div><div class="sbox-val" id="L_keyinWait">—</div></div>
            <div class="sbox"><div class="sbox-lbl">Called</div><div class="sbox-val" id="L_keyinCalled">—</div></div>
          </div>
        </div>
        <div class="row-data-cell email-cell">
          <div class="stat-grid c2">
            <div class="sbox"><div class="sbox-lbl">Waiting</div><div class="sbox-val" id="E_keyinWait">—</div></div>
            <div class="sbox"><div class="sbox-lbl">Called</div><div class="sbox-val" id="E_keyinCalled">—</div></div>
          </div>
        </div>
      </div>

      <!-- Key-In Done -->
      <div class="data-row">
        <div class="row-label-cell"><div class="row-label-text" style="color:var(--green);">Key-In Done</div></div>
        <div class="row-data-cell line-cell">
          <div class="keyin-done-wrap">
            <div style="display:flex;align-items:baseline;gap:8px;margin-bottom:8px;">
              <span class="sbox-val" style="color:var(--green)" id="L_keyinDone">—</span>
              <span style="font-family:var(--mono);font-size:22px;font-weight:600;color:var(--text3);">/ <span id="L_keyinTotal">—</span></span>
              <span class="sbox-val" style="color:var(--green);margin-left:auto;" id="L_keyinPct">—%</span>
            </div>
            <div class="keyin-bar-track">
              <div class="keyin-bar-fill" id="L_keyinBar" style="width:0%;background:linear-gradient(90deg,#06c755,#34d399);"></div>
            </div>
          </div>
        </div>
        <div class="row-data-cell email-cell">
          <div class="keyin-done-wrap">
            <div style="display:flex;align-items:baseline;gap:8px;margin-bottom:8px;">
              <span class="sbox-val" style="color:var(--green)" id="E_keyinDone">—</span>
              <span style="font-family:var(--mono);font-size:22px;font-weight:600;color:var(--text3);">/ <span id="E_keyinTotal">—</span></span>
              <span class="sbox-val" style="color:var(--green);margin-left:auto;" id="E_keyinPct">—%</span>
            </div>
            <div class="keyin-bar-track">
              <div class="keyin-bar-fill" id="E_keyinBar" style="width:0%;background:linear-gradient(90deg,#3b82f6,#60a5fa);"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div><!-- end Task Creator column wrapper -->

    <!-- SUPPORT -->
    <div class="section-block">
      <div class="section-head">
        <div class="section-head-ico" style="background:#d1fae5;">
          <svg viewBox="0 0 24 24" fill="none" stroke="#059669" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
        </div>
        <span class="section-head-title">Support</span>
      </div>

      <div class="col-header-row">
        <div class="col-header-blank"></div>
        <div class="col-header-cell line-hdr">
          <div class="col-hdr-ico"><svg viewBox="0 0 24 24" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
          <div><div class="col-hdr-name">Line</div><div class="col-hdr-sub">Waiting: <span class="col-hdr-count" id="hdrLineSup">—</span></div></div>
        </div>
        <div class="col-header-cell email-hdr">
          <div class="col-hdr-ico"><svg viewBox="0 0 24 24" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg></div>
          <div><div class="col-hdr-name">Email</div><div class="col-hdr-sub">Waiting: <span class="col-hdr-count" id="hdrEmailSup">—</span></div></div>
        </div>
      </div>

      <!-- Task Distribution -->
      <div class="data-row">
        <div class="row-label-cell"><div class="row-label-text">Task Distribution</div></div>
        <div class="row-data-cell line-cell">
          <div class="sup-donut-row">
            <svg class="sup-svg" id="L_supDonut" viewBox="0 0 76 76">
              <circle cx="38" cy="38" r="28" fill="none" stroke="#1c2333" stroke-width="8"/>
              <text x="38" y="34" text-anchor="middle" font-family="JetBrains Mono" font-size="12" font-weight="600" fill="#e8eaf0" id="L_supTotal">—</text>
              <text x="38" y="46" text-anchor="middle" font-family="Sarabun" font-size="7" fill="#4a5068">Task</text>
            </svg>
            <div class="sup-tags" id="L_supTags"></div>
          </div>
        </div>
        <div class="row-data-cell email-cell">
          <div class="sup-donut-row">
            <svg class="sup-svg" id="E_supDonut" viewBox="0 0 76 76">
              <circle cx="38" cy="38" r="28" fill="none" stroke="#1c2333" stroke-width="8"/>
              <text x="38" y="34" text-anchor="middle" font-family="JetBrains Mono" font-size="12" font-weight="600" fill="#e8eaf0" id="E_supTotal">—</text>
              <text x="38" y="46" text-anchor="middle" font-family="Sarabun" font-size="7" fill="#4a5068">Task</text>
            </svg>
            <div class="sup-tags" id="E_supTags"></div>
          </div>
        </div>
      </div>

      <!-- Support Stats -->
      <div class="data-row">
        <div class="row-label-cell"><div class="row-label-text">Support Stats</div></div>
        <div class="row-data-cell line-cell">
          <div class="smet-grid">
            <div class="smet"><div class="smet-lbl">Waiting Today</div><div class="smet-val" id="L_supWait">—</div></div>
            <div class="smet"><div class="smet-lbl">In Progress</div><div class="smet-val" id="L_supInProg">—</div></div>
          </div>
        </div>
        <div class="row-data-cell email-cell">
          <div class="smet-grid">
            <div class="smet"><div class="smet-lbl">Waiting Today</div><div class="smet-val" id="E_supWait">—</div></div>
            <div class="smet"><div class="smet-lbl">In Progress</div><div class="smet-val" id="E_supInProg">—</div></div>
          </div>
        </div>
      </div>

      <!-- Support Yesterday (all_time) -->
      <div class="data-row">
        <div class="row-label-cell" style="background:rgba(251,191,36,.08);border-right:1px solid rgba(251,191,36,.2);">
          <div class="row-label-text" style="color:#fbbf24;">Support Yesterday</div>
        </div>
        <div class="row-data-cell line-cell" style="background:rgba(251,191,36,.05);">
          <div class="sup-donut-row">
            <svg class="sup-svg" id="L_supYestDonut" viewBox="0 0 76 76">
              <circle cx="38" cy="38" r="28" fill="none" stroke="#1c2333" stroke-width="8"/>
              <text x="38" y="34" text-anchor="middle" font-family="JetBrains Mono" font-size="12" font-weight="600" fill="#e8eaf0" id="L_supYestTotal">—</text>
              <text x="38" y="46" text-anchor="middle" font-family="Sarabun" font-size="7" fill="#4a5068">Task</text>
            </svg>
            <div class="sup-tags" id="L_supYestTags"></div>
          </div>
        </div>
        <div class="row-data-cell email-cell" style="background:rgba(251,191,36,.05);">
          <div class="sup-donut-row">
            <svg class="sup-svg" id="E_supYestDonut" viewBox="0 0 76 76">
              <circle cx="38" cy="38" r="28" fill="none" stroke="#1c2333" stroke-width="8"/>
              <text x="38" y="34" text-anchor="middle" font-family="JetBrains Mono" font-size="12" font-weight="600" fill="#e8eaf0" id="E_supYestTotal">—</text>
              <text x="38" y="46" text-anchor="middle" font-family="Sarabun" font-size="7" fill="#4a5068">Task</text>
            </svg>
            <div class="sup-tags" id="E_supYestTags"></div>
          </div>
        </div>
      </div>

      <!-- Support Yesterday Stats -->
      <div class="data-row">
        <div class="row-label-cell" style="background:rgba(251,191,36,.08);border-right:1px solid rgba(251,191,36,.2);">
          <div class="row-label-text" style="color:#fbbf24;">Yesterday Stats</div>
        </div>
        <div class="row-data-cell line-cell" style="background:rgba(251,191,36,.05);">
          <div class="smet-grid">
            <div class="smet" style="background:rgba(251,191,36,.08);border-color:rgba(251,191,36,.2);"><div class="smet-lbl">Waiting</div><div class="smet-val" style="color:#fbbf24" id="L_supYestWait">—</div></div>
            <div class="smet" style="background:rgba(251,191,36,.08);border-color:rgba(251,191,36,.2);"><div class="smet-lbl">In Progress</div><div class="smet-val" style="color:#fbbf24" id="L_supYestInProg">—</div></div>
          </div>
        </div>
        <div class="row-data-cell email-cell" style="background:rgba(251,191,36,.05);">
          <div class="smet-grid">
            <div class="smet" style="background:rgba(251,191,36,.08);border-color:rgba(251,191,36,.2);"><div class="smet-lbl">Waiting</div><div class="smet-val" style="color:#fbbf24" id="E_supYestWait">—</div></div>
            <div class="smet" style="background:rgba(251,191,36,.08);border-color:rgba(251,191,36,.2);"><div class="smet-lbl">In Progress</div><div class="smet-val" style="color:#fbbf24" id="E_supYestInProg">—</div></div>
          </div>
        </div>
      </div>

      <!-- Support Result -->
      <div class="data-row">
        <div class="row-label-cell"><div class="row-label-text" style="color:var(--green);">Support Result</div></div>
        <div class="row-data-cell line-cell">
          <div class="result-grid">
            <div class="rbox g"><div class="rbox-lbl">Completed</div><div class="rbox-val" id="L_resComplete">—</div></div>
            <div class="rbox r"><div class="rbox-lbl">Closed</div><div class="rbox-val" id="L_resClose">—</div></div>
          </div>
        </div>
        <div class="row-data-cell email-cell">
          <div class="result-grid">
            <div class="rbox g"><div class="rbox-lbl">Completed</div><div class="rbox-val" id="E_resComplete">—</div></div>
            <div class="rbox r"><div class="rbox-lbl">Closed</div><div class="rbox-val" id="E_resClose">—</div></div>
          </div>
        </div>
      </div>

    </div>

    </div><!-- end side-by-side -->

  </div>
</div>

<div style="text-align:center;margin-top:24px;font-size:10px;color:var(--text3);font-family:var(--mono);" id="footer"></div>
`;

const COLORS = [
  "#3b82f6",
  "#c084fc",
  "#818cf8",
  "#34d399",
  "#f59e0b",
  "#f87171",
  "#a78bfa",
  "#fb923c",
  "#94a3b8",
  "#6ee7b7",
  "#fbbf24",
  "#e879f9",
];

export default function DashboardV2() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    // ── State (kept outside React — this page renders once, JS drives it) ──
    let refreshTimer: ReturnType<typeof setInterval> | undefined;
    let countdownTimer: ReturnType<typeof setInterval> | undefined;
    let countdownSec = 60;
    let disposed = false;

    // ── Scoped DOM helpers ──
    const byId = (id: string) =>
      root.querySelector<HTMLElement>("#" + CSS.escape(id));
    const $ = byId;
    const set = (id: string, val: any) => {
      const el = byId(id);
      if (el) el.textContent = val ?? "—";
    };
    // Order types are matched by their identifier rather than a hardcoded
    // array position. The v1 dashboard reads these positionally
    // (all_group_order_type[0] = Stock, [1] = RX), which breaks silently if
    // the API ever reorders the groups. Here we look the group up by name —
    // like every other group (e.g. support is keyed by `issue`) — and only
    // fall back to the old positional index when no identifier is present.
    const ORDER_TYPE_FALLBACK_INDEX: Record<string, number> = {
      stock: 0,
      rx: 1,
    };
    const getOT = (arr: any[], type: string) => {
      const list = arr || [];
      const match = list.find(
        (g) =>
          String(
            g?.order_type ?? g?._id ?? g?.type ?? g?.name ?? "",
          ).toLowerCase() === type,
      );
      if (match) return match.count ?? 0;
      return list[ORDER_TYPE_FALLBACK_INDEX[type]]?.count ?? 0;
    };

    // ── Fetch one channel (public endpoint — no auth) ──
    async function fetchChannel(channel: string) {
      const url = channel ? API_BASE + "?channel=" + channel : API_BASE;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error("HTTP " + res.status);
      return res.json();
    }

    async function fetchData() {
      setStatus("loading");
      try {
        const [lineData, emailData, allData, onlineData] = await Promise.all([
          fetchChannel("system"),
          fetchChannel("email"),
          fetchChannel(""),
          fetch(API_ONLINE, {
            headers: { Accept: "application/json" },
          }).then((r) => r.json()),
        ]);
        if (disposed) return;
        renderLeft(allData);
        renderChannel("L", lineData);
        renderChannel("E", emailData);
        renderOnlineUsers(onlineData);
        fetchDiablo();
        setStatus("ok");
        startCountdown();
      } catch (e: any) {
        if (disposed) return;
        setStatus("error", "เชื่อมต่อไม่ได้: " + e.message);
      }
    }

    // ── Render Online Users ──
    function renderOnlineUsers(data: any) {
      const users = data?.online_users || [];
      set("onlineCount", users.length + " คน");

      const PAGE_ORDER: Record<string, number> = { VIP: 0, STOCK: 1, RX: 2 };
      const PAGE_COLORS: Record<string, string> = {
        VIP: "#f59e0b",
        STOCK: "#3b82f6",
        RX: "#10b981",
      };

      const sorted = [...users].sort((a, b) => {
        const pa = PAGE_ORDER[a.current_page?.toUpperCase()] ?? 99;
        const pb = PAGE_ORDER[b.current_page?.toUpperCase()] ?? 99;
        return pa - pb;
      });

      const list = byId("onlineList");
      if (!list) return;
      if (!sorted.length) {
        list.innerHTML =
          '<div style="font-size:11px;color:var(--text3);text-align:center;padding:8px 0;">ไม่มีผู้ใช้ออนไลน์</div>';
        return;
      }

      list.innerHTML = sorted
        .map((u) => {
          const page = u.current_page || "—";
          const mins =
            u.online_minutes > 0 ? u.online_minutes + "m" : "Just now";
          return `
      <div class="online-user" data-page="${page}">
        <div style="flex:1;min-width:0;">
          <div class="online-name">${u.name}</div>
        </div>
        <div class="online-mins">${mins}</div>
      </div>`;
        })
        .join("");

      let lastPage: string | null = null;
      list.querySelectorAll<HTMLElement>(".online-user").forEach((el) => {
        const page = el.dataset.page || "";
        const pageColor = PAGE_COLORS[page.toUpperCase()] || "#9ca3af";
        if (page !== lastPage) {
          const badge = document.createElement("div");
          badge.style.cssText = `display:flex;align-items:center;gap:6px;margin-top:${lastPage ? "6px" : "0"};`;
          badge.innerHTML = `<span style="font-size:10px;font-weight:600;background:${pageColor}22;color:${pageColor};border:1px solid ${pageColor}55;border-radius:4px;padding:1px 8px;">${page}</span>`;
          list.insertBefore(badge, el);
          lastPage = page;
        }
      });
    }

    // ── Fetch Diabolocom Agents ──
    async function fetchDiablo() {
      try {
        const res = await fetch(DIABLO_WORKER);
        const json = await res.json();
        const agents = json?.data?.results?.[0]?.values || [];
        if (!disposed) renderDiabloAgents(agents);
      } catch {
        const el = byId("diabloList");
        if (el)
          el.innerHTML = `<div style="font-size:11px;color:var(--red);text-align:center;padding:8px 0;">เชื่อมต่อไม่ได้</div>`;
      }
    }

    function renderDiabloAgents(agents: any[]) {
      const list = byId("diabloList");
      const countEl = byId("diabloCount");
      if (!list) return;

      const STATUS_STYLE: Record<string, any> = {
        AVAILABLE: { bg: "rgba(52,211,153,.15)", color: "#34d399", dot: "#34d399" },
        WORKING: { bg: "rgba(251,191,36,.15)", color: "#fbbf24", dot: "#fbbf24" },
        PAUSE: { bg: "rgba(248,113,113,.15)", color: "#f87171", dot: "#f87171" },
        AWAY: { bg: "rgba(107,114,128,.15)", color: "#9ca3af", dot: "#6b7280" },
      };
      const ORDER: Record<string, number> = {
        AVAILABLE: 0,
        WORKING: 1,
        PAUSE: 2,
        AWAY: 3,
      };

      if (countEl) countEl.textContent = agents.length + " คน";

      if (!agents.length) {
        list.innerHTML =
          '<div style="font-size:11px;color:var(--text3);text-align:center;padding:8px 0;">ไม่มี Agent Online</div>';
        return;
      }

      const sorted = [...agents].sort(
        (a, b) => (ORDER[a.agentStatus] ?? 9) - (ORDER[b.agentStatus] ?? 9),
      );

      list.innerHTML = sorted
        .map((a) => {
          const s = STATUS_STYLE[a.agentStatus] || STATUS_STYLE["AWAY"];
          const firstName = (a.agentFirstName || "")
            .replace(/^[^|]+\|\s*/, "")
            .replace(/\w+_/, "");
          const lastName = a.agentLastName || "";
          const fullName = `${firstName} ${lastName}`.trim();
          const handled = a.agentNumberOfHandledContacts || 0;
          const occ = a.agentGlobalOccupancyRate
            ? Math.round(a.agentGlobalOccupancyRate * 100) + "%"
            : "0%";
          const customStatus = a.agentCustomStatusName || a.agentStatus;
          return `
      <div class="diablo-agent">
        <span class="diablo-presence" style="background:${s.dot};box-shadow:0 0 6px ${s.dot}66;"></span>
        <div style="flex:1;min-width:0;overflow:hidden;">
          <div class="diablo-name">${fullName}</div>
          <div class="diablo-status">${customStatus} · ${handled} calls · ${occ}</div>
        </div>
        <span class="diablo-badge" style="background:${s.bg};color:${s.color};">${a.agentStatus}</span>
      </div>`;
        })
        .join("");
    }

    function setStatus(type: "ok" | "error" | "loading", msg?: string) {
      const el = byId("statusBadge");
      if (!el) return;
      if (type === "ok") {
        el.className = "status-ok";
        el.textContent = "✓ เชื่อมต่อสำเร็จ";
      } else if (type === "error") {
        el.className = "status-err";
        el.textContent = "✕ " + (msg || "Error");
      } else {
        el.className = "status-loading";
        el.innerHTML = '<span class="spinner"></span> กำลังโหลด...';
      }
    }

    function startCountdown() {
      clearInterval(refreshTimer);
      clearInterval(countdownTimer);
      countdownSec = 60;
      countdownTimer = setInterval(() => {
        countdownSec--;
        const info = byId("refreshInfo");
        if (info) info.textContent = "refresh ใน " + countdownSec + "s";
        if (countdownSec <= 0) {
          clearInterval(countdownTimer);
          fetchData();
        }
      }, 1000);
      const lu = byId("lastUpdate");
      if (lu)
        lu.textContent =
          "อัปเดต " +
          new Date().toLocaleTimeString("th-TH", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          });
    }

    function buildDonutArcs(svgId: string, issues: any[], total: number) {
      const svg = $(svgId) as unknown as SVGSVGElement | null;
      if (!svg) return;
      svg.querySelectorAll(".arc").forEach((e) => e.remove());
      if (!total) return;
      const C = 2 * Math.PI * 28;
      let offset = 0;
      issues.forEach((item, i) => {
        if (!item.count) return;
        const dash = (item.count / total) * C;
        const arc = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "circle",
        );
        arc.setAttribute("class", "arc");
        arc.setAttribute("cx", "38");
        arc.setAttribute("cy", "38");
        arc.setAttribute("r", "28");
        arc.setAttribute("fill", "none");
        arc.setAttribute("stroke", COLORS[i % COLORS.length]);
        arc.setAttribute("stroke-width", "8");
        arc.setAttribute("stroke-dasharray", dash + " " + C);
        arc.setAttribute("stroke-dashoffset", String(-(offset - C * 0.25)));
        arc.setAttribute("stroke-linecap", "round");
        svg.insertBefore(arc, svg.querySelector("text"));
        offset += dash;
      });
    }

    function buildTags(containerId: string, issues: any[]) {
      const el = $(containerId);
      if (!el) return;
      el.innerHTML = issues
        .filter((x) => x.count > 0)
        .map(
          (item, i) => `
    <div class="stag">
      <span class="stag-name"><span class="stag-dot" style="background:${COLORS[i % COLORS.length]}"></span>${item.issue}</span>
      <span class="stag-val">${item.count}</span>
    </div>`,
        )
        .join("");
    }

    function renderLeft(d: any) {
      const today = d.today;
      const totalToday = (today.normal_all || 0) + (today.urgent_all || 0);
      set("donutTotal", totalToday);
      set("normalCount", today.normal_all);
      set("urgentCount", today.urgent_all);
      const C = 2 * Math.PI * 48;
      const nPct = totalToday ? (today.normal_all / totalToday) * C : 0;
      const uPct = totalToday ? (today.urgent_all / totalToday) * C : 0;
      $("donutNormal")?.setAttribute("stroke-dasharray", nPct + " " + C);
      $("donutUrgent")?.setAttribute("stroke-dasharray", uPct + " " + C);
      $("donutUrgent")?.setAttribute(
        "stroke-dashoffset",
        String(-(nPct - C * 0.25)),
      );
      set("urgentCompleted", d.urgent_complete_count_today);
      set(
        "urgentPending",
        (today.urgent_all || 0) - (d.urgent_complete_count_today || 0),
      );
    }

    function renderChannel(p: "L" | "E", d: any) {
      const today = d.today,
        all = d.all_time,
        tmr = d.tomorrow;

      const hdrTotal = today.normal_all || 0;
      if (p === "L") set("hdrLineTotal", hdrTotal + " tasks");
      if (p === "E") set("hdrEmailTotal", hdrTotal + " tasks");

      set(
        p + "_allStock",
        Math.max(
          0,
          getOT(all.all_group_order_type, "stock") -
            getOT(today.all_group_order_type, "stock") -
            getOT(tmr.all_group_order_type, "stock"),
        ),
      );
      set(
        p + "_allRx",
        Math.max(
          0,
          getOT(all.all_group_order_type, "rx") -
            getOT(today.all_group_order_type, "rx") -
            getOT(tmr.all_group_order_type, "rx"),
        ),
      );
      set(
        p + "_allVip",
        Math.max(0, (all.all_vip || 0) - (today.all_vip || 0) - (tmr.all_vip || 0)),
      );

      set(p + "_todayStock", getOT(today.all_group_order_type, "stock"));
      set(p + "_todayRx", getOT(today.all_group_order_type, "rx"));
      set(p + "_todayVip", today.all_vip);

      set(p + "_tmrStock", getOT(tmr.all_group_order_type, "stock"));
      set(p + "_tmrRx", getOT(tmr.all_group_order_type, "rx"));
      set(p + "_tmrVip", tmr.all_vip);

      const yesterdayStockRx = Math.max(
        0,
        getOT(all.all_group_order_type, "stock") -
          getOT(today.all_group_order_type, "stock") -
          getOT(tmr.all_group_order_type, "stock") +
          (getOT(all.all_group_order_type, "rx") -
            getOT(today.all_group_order_type, "rx") -
            getOT(tmr.all_group_order_type, "rx")),
      );
      const todayStockRx =
        getOT(today.all_group_order_type, "stock") +
        getOT(today.all_group_order_type, "rx");
      const tomorrowStockRx =
        getOT(tmr.all_group_order_type, "stock") +
        getOT(tmr.all_group_order_type, "rx");
      const keyinStockRxTotal =
        yesterdayStockRx + todayStockRx + tomorrowStockRx;
      const keyinWaiting = keyinStockRxTotal;
      const keyinCalledN = all.normal_keyin_called || 0;
      set(p + "_keyinWait", keyinStockRxTotal);
      set(p + "_keyinCalled", keyinCalledN);

      const keyinBase = today.normal_all || 0;
      const keyinDone = Math.max(0, keyinBase - keyinWaiting);
      const keyinPct =
        keyinBase > 0
          ? Math.min(100, Math.round((keyinDone / keyinBase) * 100))
          : 0;
      set(p + "_keyinDone", keyinDone);
      set(p + "_keyinTotal", keyinBase);
      set(p + "_keyinPct", keyinPct + "%");
      const barEl = byId(p + "_keyinBar");
      if (barEl) setTimeout(() => (barEl.style.width = keyinPct + "%"), 100);

      const supWait = today.waiting_support || 0;
      if (p === "L") set("hdrLineSup", supWait + " tasks");
      if (p === "E") set("hdrEmailSup", supWait + " tasks");

      set(p + "_supTotal", supWait);
      buildDonutArcs(p + "_supDonut", today.support_group_by_issue, supWait);
      buildTags(p + "_supTags", today.support_group_by_issue);

      set(p + "_supWait", today.waiting_support);
      set(p + "_supInProg", today.support_inprogress);

      set(p + "_resComplete", d.support_result_today?.complete_count);
      set(p + "_resClose", d.support_result_today?.close_count);

      const supYestIssues = (all.support_group_by_issue || []).map(
        (item: any) => {
          const todayItem = (today.support_group_by_issue || []).find(
            (x: any) => x.issue === item.issue,
          );
          return {
            issue: item.issue,
            count: Math.max(0, item.count - (todayItem?.count || 0)),
          };
        },
      );
      const supYestTotal = Math.max(
        0,
        (all.waiting_support || 0) - (today.waiting_support || 0),
      );
      const supYestWait = supYestTotal;
      const supYestInProg = all.support_inprogress || 0;

      set(p + "_supYestTotal", supYestTotal);
      set(p + "_supYestWait", supYestWait);
      set(p + "_supYestInProg", supYestInProg);
      buildDonutArcs(p + "_supYestDonut", supYestIssues, supYestTotal);
      buildTags(p + "_supYestTags", supYestIssues);
    }

    // ── Init ──
    const now = new Date();
    const dateEl = byId("dateDisplay");
    if (dateEl)
      dateEl.textContent = now.toLocaleDateString("th-TH", {
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "short",
      });
    const footer = byId("footer");
    if (footer)
      footer.textContent =
        "CS Dashboard — Essilor Luxottica  ·  Auto-refresh ทุก 60 วินาที";

    fetchData();

    // ── Cleanup ──
    return () => {
      disposed = true;
      clearInterval(refreshTimer);
      clearInterval(countdownTimer);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="dashboard-v2-root"
      dangerouslySetInnerHTML={{ __html: BODY_HTML }}
    />
  );
}
